import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import UsersFilterForm from './UsersFilterForm';
import { Button, Space, Checkbox, Popover, Table, message, Tooltip } from 'antd';
import { timeString } from '@/utils/utils';
import { fullPagerConfig } from '@/utils/property';
import { get } from '@/core/request';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { MAIN_TOOLBAR_DEDUCT_SELECTORS, useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import PageHeader from '@components/PageHeader/PageHeader';
import { buildUserTableColumns, getColumnVisibilityAfterToggleAll } from './usersTableConfig';
import './Users.less';

export default function Users() {
    // 复选框要筛选的列
    const initialColumn = [
        { name: '用户唯一ID（memcard）', checked: true, value: 0 },
        { name: '昵称', checked: true, value: 1 },
        { name: '真实姓名', checked: false, value: 2 },
        { name: '性别', checked: false, value: 3 },
        { name: '手机号', checked: true, value: 4 },
        { name: '邮箱', checked: false, value: 5 },
        { name: '用户身份', checked: true, value: 6 },
        { name: '会员类别', checked: true, value: 7 },
        { name: '单位名称', checked: true, value: 8 },
        { name: '科室', checked: true, value: 9 },
        { name: '职务', checked: true, value: 10 },
        { name: '职称', checked: true, value: 11 },
        { name: '注册端口', checked: false, value: 12 },
        { name: '用户状态', checked: false, value: 13 },
        { name: '注册时间', checked: false, value: 14 },
        { name: '认证审核时间', checked: false, value: 15 },
        { name: '最后登录时间', checked: false, value: 16 },
    ];

    // 要展示的列
    const [showColumn, setShowColumn] = useState(initialColumn);
    const AllCheckedList = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
    const defaultCheckedList = [0, 1, 4, 6, 7, 8, 9, 10, 11];
    const [checkedList, setCheckedList] = useState(defaultCheckedList);
    const [indeterminate, setIndeterminate] = useState(true);
    const [checkAll, setCheckAll] = useState(false);

    // 单机选项
    const handelShowColumn = checkedValues => {
        setCheckedList(checkedValues);
        setIndeterminate(!!checkedValues.length && checkedValues.length < AllCheckedList.length);
        setCheckAll(checkedValues.length === AllCheckedList.length);
        const res = initialColumn.map(item => ({
            ...item,
            checked: checkedValues.includes(item.value),
        }));
        setShowColumn(res);
    };

    // 点击全选
    const handleChangeField = e => {
        setCheckedList(e.target.checked ? AllCheckedList : [0]);
        setIndeterminate(false);
        setCheckAll(e.target.checked);
        const res = getColumnVisibilityAfterToggleAll(initialColumn, e.target.checked);
        setShowColumn([...res]);
    };

    // 点击重置
    const handleChangeReset = () => {
        const res = initialColumn.map(item => ({
            ...item,
            checked: defaultCheckedList.includes(item.value),
        }));
        setCheckedList(defaultCheckedList);
        setShowColumn(res);
        setIndeterminate(true);
        setCheckAll(false);
    };

    // 字段设置样式
    const pupupContent = (
        <div className="field-change-box">
            <div className="field-change-header">
                <Checkbox indeterminate={indeterminate} onChange={handleChangeField} checked={checkAll} className="field-change-header-left">
                    显示字段
                </Checkbox>
                <span className="field-change-header-right" onClick={handleChangeReset}>
                    重置
                </span>
            </div>
            <Checkbox.Group
                value={checkedList}
                style={{ width: '100%' }}
                onChange={handelShowColumn}
                className="field-change-bottom"
                options={showColumn.map(item => ({
                    value: item.value,
                    label: item.name,
                    disabled: item.value === 0,
                }))}
            />
        </div>
    );

    // 科室
    const [officeData, setOfficeData] = useState([]);
    // 职务
    const [postData, setPostData] = useState([]);
    // 职称
    const [titleData, setTitleData] = useState([]);

    // 科室 职务 职称
    useEffect(() => {
        get('user/dictionary/office/detail', { type: 2 }).then(res => {
            setOfficeData(res.data);
        });
        get('/user/dictionary/board', { type: 3 }).then(res => {
            setPostData(res.data);
        });
        get('/user/dictionary/board', { type: 4 }).then(res => {
            setTitleData(res.data);
        });
    }, []);
    // 渲染table数据的state
    const [tableData, setTableData] = useState(null);
    const [filterObj, setFilterObj] = useState({});

    // 分页设置
    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);

    // 筛选数据
    const handleFilterFinish = value => {
        setFilterObj(value);
        setCurrPage(1);
    };

    const { memCard, memDeviceType, memEmail, memExamineEndTime, memExamineStartTime, memIdentity, memJobtitle, memSex, memType, memUnit, memUsername, memState, memLastestLoginEndTime, memLastestLoginStartTime, memMobile, memNickname, memOffice, memPost, memRegistEndTime, memRegistStartTime, memIdentityType } =
        filterObj;

    const getData = useCallback(() => {
        get(`/user/data/statistics/users-detail`, {
            limit: pageSize,
            page: currPage,
            memCard,
            memDeviceType,
            memEmail,
            memExamineEndTime,
            memExamineStartTime,
            memIdentity,
            memJobtitle,
            memSex,
            memType,
            memUnit,
            memUsername,
            memState,
            memLastestLoginEndTime,
            memLastestLoginStartTime,
            memMobile,
            memNickname,
            memOffice,
            memPost,
            memRegistEndTime,
            memRegistStartTime,
            memIdentityType,
        }).then(res => {
            if (res.status_code === 200) {
                const { list } = res.data;
                list?.forEach(item => {
                    item.key = item.memCard;
                });
                setTableData(res.data);
            }
        });
    }, [
        pageSize,
        currPage,
        memCard,
        memDeviceType,
        memEmail,
        memExamineEndTime,
        memExamineStartTime,
        memIdentity,
        memJobtitle,
        memSex,
        memType,
        memUnit,
        memUsername,
        memState,
        memLastestLoginEndTime,
        memLastestLoginStartTime,
        memMobile,
        memNickname,
        memOffice,
        memPost,
        memRegistEndTime,
        memRegistStartTime,
        memIdentityType,
    ]);

    // 渲染table数据
    useEffect(() => {
        getData();
    }, [getData]);

    // 导出数据
    const handleExportFile = () => {
        if (tableData && tableData.totalCount > 10000) {
            message.error('当前筛选数据超过1万条，无法导出!');
            return;
        }
        get(
            `/user/data/statistics/users-detail-download`,
            {
                limit: pageSize,
                page: currPage,
                memCard,
                memDeviceType,
                memEmail,
                memExamineEndTime,
                memExamineStartTime,
                memIdentity,
                memJobtitle,
                memSex,
                memType,
                memUnit,
                memUsername,
                memLastestLoginEndTime,
                memLastestLoginStartTime,
                memMobile,
                memNickname,
                memOffice,
                memPost,
                memRegistEndTime,
                memRegistStartTime,
                memIdentityType,
            },
            { responseType: 'blob' },
        ).then(res => {
            if (res.size !== 0) {
                const date = new Date();
                const dateString = timeString(date);
                message.success('请稍后，正在导出');
                const fileName = `用户列表${dateString}.xlsx`;
                const a = document.createElement('a');
                const event = new MouseEvent('click');
                a.download = fileName;
                const objectUrl = URL.createObjectURL(res);
                a.href = objectUrl;
                a.dispatchEvent(event);
                URL.revokeObjectURL(objectUrl);
            } else {
                message.warning('当前列表没有数据，无法导出');
            }
        });
    };

    const [refreshBool, setRefreshBool] = useState(false);

    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    const tableColumns = buildUserTableColumns(showColumn);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData?.totalCount,
        onShowSizeChange: (_current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    const tableRegionRef = useRef(null);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    useEffect(() => {
        if (refreshBool) {
            setRefreshBool(false);
            if (currPage === 1) {
                getData();
            } else {
                changeCurrPage(1);
            }
        }
    }, [refreshBool, currPage, changeCurrPage, getData]);

    // 点击刷新
    const handleRefresh = () => {
        message.info('正在刷新', 1);
        setRefreshBool(true);
    };

    return (
        <div className="page-container list-page-container users-page">
            <PageHeader title="用户管理" breadcrumb={[{ label: '用户中心' }, { label: '用户管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <UsersFilterForm setCurrPage={setCurrPage} officeData={officeData} postData={postData} titleData={titleData} handleFilterFinish={handleFilterFinish} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container users-table-region">
                <div className="main-toolbar users-table-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="default" onClick={handleExportFile} icon={<i className="bi bi-download"></i>}>
                                导出
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<i className="bi bi-arrow-clockwise"></i>} aria-label="刷新列表" />
                            </Tooltip>
                            <Popover placement="bottomRight" content={pupupContent} trigger="click">
                                <Button type="default" icon={<i className="bi bi-gear-fill"></i>} aria-label="字段设置" />
                            </Popover>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap users-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always enterprise-table' : 'enterprise-table'}
                        columns={tableColumns}
                        dataSource={tableData?.list}
                        pagination={paginationProps}
                        rowKey={record => record.memCard}
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        sticky
                        size="medium"
                        bordered
                    />
                </div>
            </div>
        </div>
    );
}
